"use client";

import { useEffect, useState } from "react";
import clsx from "clsx";
import { Monogram } from "@/components/persona/Monogram";
import { advanceScreen } from "@/lib/presenter/advanceScreen";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Screen 1.4 — Live Verification Call. Two-pane video room (no actual
 * camera/mic). Eva (the verification officer) prompts Al Madar through a
 * scripted attestation sequence. Digital signature canvas captures input.
 *
 * On completion: LiveVerificationCompleted chip with officerLei + sessionRef
 * appears. The OrbID issuance fee step (1.5) gates on this.
 *
 * Why this matters: the only human-in-the-loop moment in the entire
 * onboarding. Signals to investors that Orbitra Global's identity layer is not
 * just OCR + GLEIF API calls.
 */
export function LiveVerificationCall({ phase = "challenge", className }: {
  phase?: "intro" | "id" | "challenge" | "signature" | "complete";
  className?: string;
}) {
  const t = useTranslations("t1Registration");
  // Gate the end-of-call popup behind a short delay AFTER the screen
  // animation completes (phase === "complete"). The audience first reads
  // the LiveVerificationCompleted chip + locked signature, then the
  // confirmation popup slides in as the next beat — never simultaneously.
  const [popupOpen, setPopupOpen] = useState(false);
  useEffect(() => {
    if (phase !== "complete") { setPopupOpen(false); return; }
    const timer = setTimeout(() => setPopupOpen(true), 1200);
    return () => clearTimeout(timer);
  }, [phase]);

  return (
    <div className={clsx("relative flex h-full flex-col gap-4", className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="grid h-7 w-7 place-items-center rounded-full bg-policy-fail/20 text-[10px] font-semibold text-policy-fail animate-pulse">
            {t("callRecTag")}
          </span>
          <p className="font-display text-base font-semibold text-orbitra-paper">{t("callTitle")}</p>
        </div>
        <p className="font-mono text-[10px] uppercase tracking-wider text-orbitra-muted">
          {t("callSessionInfo")}
        </p>
      </div>

      <div className="grid flex-1 grid-cols-2 gap-4">
        <VideoPane name={t("callAlMadarLabel")} initials="FA" flag="AE" photo="/faces/Faisal.png" caption={
          phase === "id"        ? t("callHoldingId") :
          phase === "challenge" ? t("callReadingChallenge") :
                                  t("callCameraConnected")
        } />
        <VideoPane name={t("callEvaLabel")} initials="E" flag="" verified photo="/faces/Eva.png" caption={t("callEvaCaption")} />
      </div>

      <div className="glass p-4">
        <p className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("callAgentPrompt")}</p>
        <ol className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
          <Step n={1} label={t("callPromptName")}      done={phase !== "intro"} />
          <Step n={2} label={t("callPromptId")}        done={phase === "challenge" || phase === "signature" || phase === "complete"} />
          <Step n={3} label={t("callPromptChallenge")} done={phase === "signature" || phase === "complete"} active={phase === "challenge"} />
        </ol>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-md border border-state-verified/20 bg-orbitra-ink/40 p-3">
            <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{t("callChallengeLabel")}</p>
            <p className="mt-0.5 font-mono text-base text-state-verified" dir="ltr">{t("callChallengePhrase")}</p>
          </div>
          <SignatureCanvas locked={phase === "complete"} />
        </div>

        {phase === "complete" && (
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-policy-pass/50 bg-policy-pass/15 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-policy-pass">
            {t("callCompletedChip")}
          </div>
        )}
      </div>

      {/* End-of-call confirmation popup — appears ~1.2s AFTER the screen
       *  animation completes (phase === "complete"), so the audience reads
       *  the LiveVerificationCompleted chip + locked signature first, then
       *  the modal slides in. Click OK advances to 1.5 (Real-Time
       *  Catalogue). Moved here from 1.5 per the earlier amendment so the
       *  handover reads as one continuous beat. */}
      {popupOpen && (
        <div
          className="absolute inset-0 z-40 grid place-items-center bg-orbitra-ink/70 backdrop-blur-sm"
          onClick={advanceScreen}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="orbid-created-title"
            className="relative w-[min(560px,92vw)] rounded-md border-2 border-state-verified bg-orbitra-ink/95 p-5 shadow-[0_0_60px_var(--state-verified-glow),0_0_18px_var(--state-verified)] backdrop-blur-md"
            onClick={(e) => e.stopPropagation()}
          >
            <span
              aria-hidden="true"
              className="absolute -top-3 start-4 inline-flex items-center gap-1 rounded-full border border-state-verified bg-orbitra-ink px-3 py-0.5 font-display text-[10px] font-semibold uppercase tracking-[0.22em] text-state-verified shadow-[0_0_18px_var(--state-verified-glow)]"
            >
              {t("callPopupBrandTag")}
            </span>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">
              {t("callPopupKicker")}
            </p>
            <h3 id="orbid-created-title" className="mt-1 font-display text-lg font-semibold text-orbitra-paper">
              {t("callPopupTitle")}
            </h3>
            <p className="mt-2 text-[12px] text-orbitra-teal-soft">
              {t("callPopupBody")}
            </p>

            <div className="mt-4 rounded-md border border-state-verified/30 bg-orbitra-ink/40 p-3 text-[11px]">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{t("callFieldTempId")}</span>
                <span className="font-mono text-orbitra-paper" dir="ltr">TMP-ALMADAR-2026-0742</span>
              </div>
              <div className="mt-1.5 flex items-baseline justify-between gap-2">
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{t("callFieldHolder")}</span>
                <span className="text-orbitra-paper">{t("callHolderValue")}</span>
              </div>
              <div className="mt-1.5 flex items-baseline justify-between gap-2">
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{t("callFieldJurisdiction")}</span>
                <span className="text-orbitra-paper">{t("callJurisdictionValue")}</span>
              </div>
              <div className="mt-1.5 flex items-baseline justify-between gap-2">
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{t("callFieldCreated")}</span>
                <span className="font-mono text-orbitra-paper" dir="ltr">{t("callPopupCreatedValue")}</span>
              </div>
              <div className="mt-1.5 flex items-baseline justify-between gap-2">
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{t("callFieldRealLei")}</span>
                <span className="text-orbitra-muted italic">{t("callRealLeiValue")}</span>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <p className="text-[10px] text-orbitra-muted">
                {t("callPopupOkHint")}
              </p>
              <button
                type="button"
                onClick={advanceScreen}
                className="cta-primary px-5 py-2 font-display text-sm font-semibold"
              >
                {t("callPopupOkCta")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VideoPane({ name, initials, flag, caption, verified, photo }: { name: string; initials: string; flag: string; caption: string; verified?: boolean; photo?: string }) {
  return (
    <div className="relative flex aspect-video items-center justify-center overflow-hidden rounded-md border border-state-verified/30 bg-gradient-to-br from-orbitra-teal-deep via-orbitra-ink to-orbitra-teal-deep">
      {photo ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={photo} alt={name} className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div className="absolute inset-0 grid place-items-center">
          <Monogram name={name} glyph={initials} flag={flag} size={64} accent={verified ? "var(--state-verified)" : "var(--state-cold)"} />
        </div>
      )}
      <span className="absolute bottom-2 start-2 rounded-md bg-orbitra-ink/80 px-2 py-1 text-[10px] text-orbitra-paper">
        {name}
      </span>
      <span className="absolute bottom-2 end-2 rounded-md bg-orbitra-ink/80 px-2 py-1 text-[9px] uppercase tracking-wider text-orbitra-muted">
        {caption}
      </span>
    </div>
  );
}

function Step({ n, label, done, active }: { n: number; label: string; done: boolean; active?: boolean }) {
  return (
    <li className={clsx(
      "flex items-start gap-2 rounded-md border px-2 py-1.5",
      done   && "border-policy-pass/50 bg-policy-pass/10 text-policy-pass",
      active && "border-state-verified bg-state-verified/15 text-state-verified shadow-[0_0_18px_var(--state-verified-glow)]",
      !done && !active && "border-orbitra-muted/20 text-orbitra-muted",
    )}>
      <span className="font-mono text-[10px] font-semibold">{n}</span>
      <span className="leading-tight">{label}</span>
      {done && <span className="ms-auto text-[10px]">✓</span>}
    </li>
  );
}

function SignatureCanvas({ locked }: { locked: boolean }) {
  const t = useTranslations("t1Registration");
  return (
    <div className={clsx(
      "relative rounded-md border bg-orbitra-ink/40 p-3",
      locked ? "border-policy-pass/40" : "border-state-verified/30",
    )}>
      <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{t("callDigitalSignature")}</p>
      <div className="mt-1 grid h-12 place-items-center font-display text-xl italic text-state-verified" dir="ltr">
        {t("amlSignatoryName")}
      </div>
      <div className="absolute inset-x-3 bottom-1.5 border-b border-orbitra-muted/30" />
    </div>
  );
}
